import React, { useContext, useState } from 'react';
import { format } from 'date-fns';
import gql from 'graphql-tag';
import { chunk } from 'lodash';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { Context as SettingsContext } from "components/setting/context";
import { Context } from './context';
import { htmlToText, deepFlatten } from 'utils/helpers';
import { SELECTED_ORDER_MUTATION, SITES } from 'graphql/entry';
import { Headers, UserKeys, OrderKeys, OrderTypeCodeTotalKey, ShippingKeys, PaymentKeys, taxKey, OfferKeys, ShippingHeader, ProductHeader, ProductDetail, ProductExportHeader, ProductExportExtraHeader, ShippingExtraHeader } from './constants';
import { SearchExportOrders_search_item_Order, SearchExportOrders_search_item_Order_items as OrderItems } from 'graphql/types/SearchExportOrders';
/**
 * A React Hook to handle to duplicate Event
 * @return {object.duplicateLoading} duplicateLoading - Boolean value to show loading state while till process completed
 * @return {object.duplicateEvent} duplicateEvent - A callback that handles event input data to duplicate
  */
const useExportData = () => {
  const { state: { exportVariables } } = useContext(Context);
  const client = useApolloClient();
  const { data, error, loading } = useQuery(SITES);
  const { settings } = useContext(SettingsContext);
  const customFieldNames =
    settings["order.exports.user.demographic.fields"]?.stringValue;
  const customFieldHeaders = customFieldNames
    ? customFieldNames.trim().split(",")
    : customFieldNames;

  const { isAllSelect } = exportVariables;

  /**
   * group custom field by their types and set multiple value comma listed
   * then only add value of those custom fields to csv which are configured in system setting
   * ex: system setting order.exports.user.demographic.fields value is profession
   * then profession will be column and value will be value of user's custom field where type is profession
   */
  function processCustomFields(value) {
    if (value && value.length) {
      const groupedCustomFields = {};
      value.forEach((element) => {
        const key = element.type.name?.toLowerCase()?.trim();
        if (groupedCustomFields.hasOwnProperty(key)) {
          groupedCustomFields[key?.toLowerCase()] = `${
            groupedCustomFields[key?.toLowerCase()]
          }, ${element.value}`;
        } else {
          groupedCustomFields[key?.toLowerCase()] = element.value;
        }
      });

      return customFieldHeaders.map((header) => {
        const key = header?.toLowerCase()?.trim();
        return groupedCustomFields.hasOwnProperty(key)
          ? groupedCustomFields[key]
          : "";
      });
    } else {
      return customFieldHeaders.map(() => "");
    }
  }

  /**
   * manipulateData customise and reorder row data as per csv column order
   * @param {SearchExportOrders_search_item_Order} row contains particular table row data
   * @param {OrderItems} orderitem contains paticular order item data
   * @param {number} hasMultipleItem contains index of order item
   */
  function manipulateData(row: SearchExportOrders_search_item_Order, productItems?: number) {
    // Fetch userid and customer number column data
    const orderUserData = UserKeys.map((key: string) => {
      return (row.user && row.user[key]) ? row.user[key] : '';
    });
    // Fetch order data e.g firstname, postalcode, address etc.
    const orderBasicData = OrderKeys.map((key: string) => {
      if (key === 'country' && row[key]) {
        return row[key].iso;
      }
      if (key === 'state' && row[key]) {
        return row[key].name;
      }
      if (key === 'createdAt' && row[key]) {
        return format(new Date(String(row[key])), 'LLLL dd, yyyy');
      }
      if (key === 'amountPaid' && row[key]) {
        var res = row[key].replace("USD", "");
        return `$${res}`;
      }
      return (row[key]) ? row[key] : '';
    });
    // Fetch transactionid and card number digit column data
    const payments = PaymentKeys.map((key: string) => {
      return (row.payment && row.payment[key]) ? row?.payment?.[key] : '';
    });
    // Fetch refferal code, totalShippingCost and shippingtype
    const orderTypeCodeTotal = OrderTypeCodeTotalKey.map((key: string) => {
      if (key === 'totalShippingCost' && row[key]) {
        var res = row[key].replace("USD", "");
        return `$${res}`;
      }
      return (row[key]) ? row[key] : '';
    });
    // Fetch shipping data e.g firstname, postalcode, addresses etc column data
    const shipping = ShippingKeys.map((key: string, index: number) => {
      return (row.shippingAddress) ? row.shippingAddress[key] : '';
    });
    // Fetch sales tax column data
    const tax = taxKey.map((key: string) => {
      const tax = row[key] && row[key] || '';
      var res = tax.replace("USD", "");
      return (res) ? `$${res}` : '';
    });
    let products = [];
    const productsLastColumnsData = [];
    const shippingCostExtraColumnsData = [];
    // Fetch product detail e.g productid, product name, cost and quantity column data
    if (productItems > 0) {
      for (let i = 0; i < productItems; i++) {
        let res = row?.items[i]?.cost.replace("USD", "") || '';
        const cost = (res) ? `$${res}` : '';
        const discount = row?.items[i]?.discount.replace("USD", "") || "";
        const discount_value = discount ? `$${discount}` : "$0";
        const revised_price = row?.items[i]?.discount
          ? parseFloat(row?.items[i]?.cost) -
            parseFloat(row?.items[i]?.discount)
          : row?.items[i]?.cost;
        const revised_price_value = !isNaN(Number(revised_price)) && row?.items[i] ? `$${revised_price}` : "$0";
        const salesTax = 
          row?.items[i]?.salesTax?.replace("USD", "") || "0";
        const salesTaxValue = row?.items[i] && salesTax ? `$${salesTax}` : "$0"
        const shippingCost = 
          row?.items[i]?.shippingCost?.replace("USD", "") || "0";
        const shippingCostValue = row?.items[i] && shippingCost ? `$${shippingCost}` : "$0"
        shippingCostExtraColumnsData.push(shippingCostValue)
        products.push(
          row?.items[i]?.product?.oid || '',
          row?.items[i]?.product?.name || '',
          row?.items[i]?.quantity || '',
          cost || '',
          (row?.items[i]?.product?.weight) ? `${row?.items[i]?.product?.weight} ${row?.items[i]?.product?.weightUnit}` : '',
          row?.items[i]?.product?.internalId || '',
          row?.items[i]?.product?.externalId || '',
        )
        productsLastColumnsData.push(
          discount_value || "",
          revised_price_value || "",
          salesTaxValue || "",
        )
      }
    }
    // Fetch promocode column data
    const promoCode = OfferKeys.map((key: string) => {
      return row.offers?.map((itemKey) => {
        return itemKey.code || '';
      });
    });
    const notes = ProductDetail.map((key: string) => {
      return (row[key]) ? htmlToText(row[key]) : '';
    });

    const customFieldsData = customFieldHeaders?.length
      ? processCustomFields(row?.user?.customFields)
      : [];

    return [data?.context?.host, ...orderUserData, ...orderBasicData, ...payments, ...promoCode, ...orderTypeCodeTotal, ...tax, ...products, ...shipping, ...notes, ...productsLastColumnsData, ...customFieldsData, ...shippingCostExtraColumnsData];
  }
  function manipulateOrderDetail(row: SearchExportOrders_search_item_Order, orderItem?: any, bundleProductItem?: any, bundleProduct?: boolean, bundleProductItemQty?: number) {
    // Fetch userid and customer number column data
    const orderUserData = UserKeys.map((key: string) => {
      return (row.user && row.user[key]) ? row.user[key] : '';
    });
    // Fetch order data e.g firstname, postalcode, address etc.
    const orderBasicData = OrderKeys.map((key: string) => {
      if (key === 'country' && row[key]) {
        return row[key].iso;
      }
      if (key === 'state' && row[key]) {
        return row[key].name;
      }
      if (key === 'createdAt' && row[key]) {
        return format(new Date(String(row[key])), 'LLLL dd, yyyy');
      }
      if (key === 'amountPaid' && row[key]) {
        const res = row[key].replace("USD", "");
        return `$${res}`;
      }
      return (row[key]) ? row[key] : '';
    });
    // Fetch transactionid and card number digit column data
    const payments = PaymentKeys.map((key: string) => {
      return (row.payment && row.payment[key]) ? row?.payment?.[key] : '';
    });
    // Fetch refferal code, totalShippingCost and shippingtype
    const orderTypeCodeTotal = OrderTypeCodeTotalKey.map((key: string) => {
      if (key === 'totalShippingCost' && row[key]) {
        const res = row[key].replace("USD", "");
        return (bundleProductItem) ? `$0` : `$${res}`;
      }
      return (bundleProductItem) ? '' : (row[key]) ? row[key] : '';
    });
    // Fetch shipping data e.g firstname, postalcode, addresses etc column data
    const shipping = ShippingKeys.map((key: string, index: number) => {
      if (row.shippingAddress == null) {
        if (key === 'country' && row[key]) {
          return row[key].iso;
        }
        if (key === 'state' && row[key]) {
          return row[key].name;
        }
        return (row[key]) ? row[key] : '';
      } else {
        return (row.shippingAddress) ? row.shippingAddress[key] : '';
      }
    });
    // Fetch sales tax column data
    const tax = taxKey.map((key: string) => {
      const tax = row[key] && row[key] || '';
      const res = tax.replace("USD", "");
      return (bundleProductItem) ? `$0` : (res) ? `$${res}` : '';
    });
    let products = [];
    const productLastColumnsData = [];
    // Fetch product detail e.g productid, product name, cost and quantity column data
    const res = (bundleProductItem) ? '0' : orderItem?.cost.replace("USD", "") || '';
    const cost = (res) ? `$${res}` : '';
    const discount = bundleProductItem ? "0" : orderItem?.discount.replace("USD", "") || "";
    const discount_value = discount ? `$${discount}` : "$0";
    const revised_price = orderItem?.discount !== "undefined" ? (orderItem?.discount
      ? parseFloat(orderItem.cost) - parseFloat(orderItem.discount)
      : parseFloat(orderItem.cost)) : '0';
    const revised_price_value = !isNaN(Number(revised_price)) && orderItem ? `$${revised_price}` : "$0";
    const oz = (bundleProduct) ? 0 : (bundleProductItem) ? `${(orderItem?.weight * 16)}` : (orderItem?.product?.weight) ? `${(orderItem?.product?.weight * 16)}` : '';
    products.push(
      (bundleProductItem) ? orderItem?.oid : orderItem?.product?.oid || '',
      (bundleProductItem) ? orderItem?.name : orderItem?.product?.name || '',
      (bundleProductItem) ? bundleProductItemQty : orderItem?.quantity || '',
      cost || '',
      (bundleProductItem) ? (orderItem?.weight) ? orderItem?.weight : '' : (orderItem?.product?.weight) ? `${orderItem?.product?.weight}` : '',
      (bundleProductItem) ? orderItem?.internalId : orderItem?.product?.internalId || '',
      (bundleProductItem) ? orderItem?.externalId : orderItem?.product?.externalId || '',
      oz,
    )
    const salesTax = 
      orderItem?.salesTax?.replace("USD", "") || "0";
    const salesTaxValue = salesTax ? `$${salesTax}` : "$0"
    productLastColumnsData.push(
      discount_value || "",
      revised_price_value || "",
      salesTaxValue
    )
    // Fetch promocode column data
    const promoCode = OfferKeys.map((key: string) => {
      return row.offers?.map((itemKey) => {
        return itemKey.code || '';
      });
    });
    const notes = ProductDetail.map((key: string) => {
      return (row[key]) ? htmlToText(row[key]) : '';
    });

    const customFieldsData = customFieldHeaders?.length
      ? processCustomFields(row?.user?.customFields)
      : [];
  
    const shippingCost = 
      orderItem?.shippingCost?.replace("USD", "") || "0";
    const shippingCostValue = shippingCost ? `$${shippingCost}` : "$0"

    return [data?.context?.host, ...orderUserData, ...orderBasicData, ...payments, ...promoCode, ...orderTypeCodeTotal, ...tax, ...products, ...shipping, ...notes, ...productLastColumnsData, ...customFieldsData, shippingCostValue];
  }
  type DataType = string[] | string[][];
  /**
   * runDataQuery handles run time query generated by seleted rows ids
   * @param {Array<{[key: string]: unknown }>} finalData contains query, parameters and variable to get data
   * @returns An array list to export into csv
   */
  async function runDataQuery(finalData) {
    let queryData = [];
    try {
      const mutatestring = gql`
          query orderQuery(${finalData.param}){
          ${finalData.query}
          } `;
      const { data } = await client.query({
        query: mutatestring,
        variables: finalData.variables,
      });
      for (let index = 0; index < finalData.counter; index++) {
        let object = data[`order${index}`]
        queryData.push(object);
      }
    } catch (err) {
      console.log(err)
    }
    return queryData;
  }
  //works as callback function into export
  async function getData(recordData, selectedRow) {
    let dynamicData = [];
    if ((selectedRow && isAllSelect) || (selectedRow && !isAllSelect && recordData.length > 10)) {
      const chunkedList = chunk(recordData, 10);
      for (let i = 0; i < chunkedList.length; i++) {
        const finalData = SELECTED_ORDER_MUTATION(chunkedList[i], {}, '', 0, '', Boolean(customFieldHeaders?.length));
        dynamicData = [...dynamicData, ...await runDataQuery(finalData)];
        const progress = Math.round((i * 100) / chunkedList.length);
      }
    } else if (selectedRow && !isAllSelect) {
      const finalData = SELECTED_ORDER_MUTATION(recordData, {}, '', 0, '', Boolean(customFieldHeaders?.length));
      dynamicData = await runDataQuery(finalData);
      // setCompleted(100);
    } else { }
    const exportDataLength = (selectedRow) ? dynamicData : recordData;
    const productItems = Math.max(...exportDataLength.map(item => (selectedRow) ? item['items'].length : item['item']['items'].length));
    // generate dynamic header name of product fields
    let dynamicHeader = [];
    const dynamicLastHeaders = [];
    const dynamicLastHeaders1 = [];
    for (let i = 1; i <= productItems; i++) {
      let headerString = `PRODUCT `;
      dynamicHeader.push(`${headerString}ID ${i}`, `${headerString}NAME ${i}`, `${headerString}QTY ${i}`, `${headerString}COST ${i}`, `${headerString} WEIGHT ${i}`, `${headerString} INTERNAL ID ${i}`, `${headerString} EXTERNAL ID ${i}`);
      dynamicLastHeaders.push(`${headerString}DISCOUNT ${i}`, `${headerString}REVISED PRICE ${i}`, `${headerString}TAX ${i}`);
      dynamicLastHeaders1.push(`${headerString}SHIPPING COST ${i}`)
    }
    const data: DataType = (selectedRow) ? (
      dynamicData.map((value) => manipulateData(value, productItems))
    ) : (
      recordData.map((value) => manipulateData(value.item, productItems))
    );

    const customFieldHeader = customFieldHeaders?.length
      ? customFieldHeaders.map((value) => value?.toUpperCase()?.trim())
      : [];

    const headersState = [...Headers, ...dynamicHeader, ...ShippingHeader, ...ProductHeader, ...dynamicLastHeaders, ...customFieldHeader, ...dynamicLastHeaders1];
    const exportData = deepFlatten(data, headersState.length);
    return [headersState, exportData];
  }
  async function getOrderExportDetail(recordData, selectedRow) {
    let dynamicData = [];
    if ((selectedRow && isAllSelect) || (selectedRow && !isAllSelect && recordData.length > 10)) {
      const chunkedList = chunk(recordData, 10);
      for (let i = 0; i < chunkedList.length; i++) {
        const finalData = SELECTED_ORDER_MUTATION(chunkedList[i], {}, '', 0, '', Boolean(customFieldHeaders?.length));
        dynamicData = [...dynamicData, ...await runDataQuery(finalData)];
      }
    } else if (selectedRow && !isAllSelect) {
      const finalData = SELECTED_ORDER_MUTATION(recordData, {}, '', 0, '', Boolean(customFieldHeaders?.length));
      dynamicData = await runDataQuery(finalData);
    } else { }
    let collectedCSVROW = [];
    const data: DataType = (selectedRow) ? (
      dynamicData.map((value) => value.items?.map(orderItem => {
        if (orderItem.product.productType == 'Bundle') {
          collectedCSVROW.push(manipulateOrderDetail(value, orderItem, false, true))
        }
        if (orderItem.product?.bundledProducts.length > 0) {
          return orderItem.product?.bundledProducts.map(bP => {
            const bPL = manipulateOrderDetail(value, bP, true, false, orderItem.quantity)
            collectedCSVROW.push(bPL)
          })
        } else {
          collectedCSVROW.push(manipulateOrderDetail(value, orderItem, false, false))
        }
      }))
    ) : (
      recordData.map((value) => value.item.items?.map(orderItem => {
        if (orderItem.product.productType == 'Bundle') {
          collectedCSVROW.push(manipulateOrderDetail(value.item, orderItem, false, true))
        }
        if (orderItem.product?.bundledProducts.length > 0) {
          return orderItem.product?.bundledProducts.map(bP => {
            const bPL = manipulateOrderDetail(value.item, bP, true, false, orderItem.quantity)
            collectedCSVROW.push(bPL)
          })
        } else {
          collectedCSVROW.push(manipulateOrderDetail(value.item, orderItem, false, false))
        }
      }))
    );

    const customFieldHeader = customFieldHeaders?.length
      ? customFieldHeaders.map((value) => value?.toUpperCase()?.trim())
      : [];

    const headersState = [...Headers, ...ProductExportHeader, ...ShippingHeader, ...ProductHeader, ...ProductExportExtraHeader, ...customFieldHeader, ...ShippingExtraHeader];
    const exportData = deepFlatten(collectedCSVROW, headersState.length);
    return [headersState, exportData];
  }
  return {
    getData,
    getOrderExportDetail,
  }
}
export default useExportData;